import { useState, useCallback, useRef } from "react";
import { applySchedulerTemplate } from "repositories/scheduler-repository";
import { handleError, handleSuccess } from "utils";
import { buildApplySchedulerTemplatePayload } from "./eventsWorkspaceApplyTemplate.utils";

/**
 * Add from Saved Template for Event Workspace (Daily, Weekly, and Monthly).
 * Opens the template picker for the active view’s ViewType and applies the
 * selected template into the currently selected period as new draft records
 * (existing items unchanged; original template is not mutated).
 * @param {{
 *   t: Function,
 *   calendarViewId: string,
 *   currentDate: Date,
 *   ownerId: number|null|undefined,
 *   onRefresh?: () => void,
 * }} options
 */
export function useEventsWorkspaceApplyTemplate({
  t,
  calendarViewId,
  currentDate,
  ownerId,
  onRefresh,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const isApplyingRef = useRef(false);

  const canApplyTemplate = Boolean(ownerId) && !isApplying;

  const closeModal = useCallback(() => {
    if (isApplyingRef.current) return;
    setIsModalOpen(false);
  }, []);

  const openApplyTemplateModal = useCallback(() => {
    if (!ownerId || isApplyingRef.current) return;
    setIsModalOpen(true);
  }, [ownerId]);

  const executeApply = useCallback(
    async (templateId) => {
      if (!ownerId || isApplyingRef.current || !isModalOpen) return false;
      if (templateId == null) {
        handleError(t("scheduler.applyTemplateSelectRequired"));
        return false;
      }

      isApplyingRef.current = true;
      setIsApplying(true);
      try {
        const payload = buildApplySchedulerTemplatePayload({
          ownerId,
          templateId,
          calendarViewId,
          currentDate,
        });
        await applySchedulerTemplate(payload);
        handleSuccess(t("scheduler.applyTemplateSuccess"));
        setIsModalOpen(false);
        onRefresh?.();
        return true;
      } catch (error) {
        handleError(error?.message || error);
        return false;
      } finally {
        isApplyingRef.current = false;
        setIsApplying(false);
      }
    },
    [ownerId, isModalOpen, calendarViewId, currentDate, t, onRefresh],
  );

  return {
    isModalOpen,
    isApplying,
    canApplyTemplate,
    openApplyTemplateModal,
    closeModal,
    executeApply,
  };
}
